import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, ArrowLeft, Trash2 } from "lucide-react";
import { feesApportionPartyApi, managementCompanyApi, associationApi } from "@/services/api";
import { FeesApportionParty, ManagementCompany, Association } from "@/models";
import { extractDataArray } from "@/utils/apiUtils";

import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type EntityType = 'none' | 'management_company' | 'association' | 'platform';

interface EntityOption {
  id: number;
  name: string;
}

const FeesApportionPartyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const isNewParty = id === "new";
  const partyId = isNewParty ? undefined : parseInt(id || "0");
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [party, setParty] = useState<Partial<FeesApportionParty>>({
    entity_name: "none",
    description: null,
    entity_id: null
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [partyError, setPartyError] = useState<Error | null>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>('none');

  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchParty = async () => {
      if (!partyId || isNewParty) return;
      
      try {
        setIsLoading(true);
        const data = await feesApportionPartyApi.getById(partyId);
        setParty({
          id: data.id,
          entity_name: data.entity_name,
          description: data.description,
          entity_id: data.entity_id
        });
        
        if (data.entity_name === 'management_companies') {
          setSelectedEntityType('management_company');
        } else if (data.entity_name === 'associations') {
          setSelectedEntityType('association');
        } else {
          setSelectedEntityType('none');
        }
      } catch (error: any) {
        setPartyError(error);
        toast({
          title: "Error loading fees apportion party",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchParty();
  }, [partyId, isNewParty, toast]);

  const { 
    data: managementCompanies = [],
    isLoading: loadingManagementCompanies
  } = useQuery({
    queryKey: ['management-companies'],
    queryFn: () => managementCompanyApi.list(),
    enabled: selectedEntityType === 'management_company',
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error loading management companies",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  });

  const { 
    data: associationsResponse = [],
    isLoading: isLoadingAssociations
  } = useQuery({
    queryKey: ['associations', {}],
    queryFn: associationApi.list,
    enabled: selectedEntityType === 'association',
    meta: {
      onError: (error: Error) => {
        toast({
          title: 'Error loading associations',
          description: error.message,
          variant: 'destructive'
        });
      }
    }
  });

  const associations = extractDataArray(associationsResponse);

  const handleEntityTypeChange = (value: string) => {
    const entityType = value as EntityType;
    setSelectedEntityType(entityType);
    
    setParty(prev => ({
      ...prev,
      entity_name: entityType === 'management_company' ? 'management_companies' : 
                  entityType === 'association' ? 'associations' :
                  entityType === 'platform' ? 'platform' : 'none',
      entity_id: null
    }));
  };

  const handleEntitySelection = (value: string) => {
    setParty(prev => ({
      ...prev,
      entity_id: parseInt(value)
    }));
  };

  const handleSave = async () => {
    if (!party.entity_name) {
      toast({
        title: "Validation Error",
        description: "Entity type is required",
        variant: "destructive"
      });
      return;
    }

    if (selectedEntityType !== 'none' && !party.entity_id) {
      console.warn("Saving party with entity type but no entity selected");
    }

    setLoading(true);
    try {
      console.log("Saving party data:", party);
      
      const partyData = {
        ...party,
        entity_name: party.entity_name,
        entity_id: selectedEntityType === 'none' ? null : party.entity_id
      };
      
      if (isNewParty) {
        const newParty = await feesApportionPartyApi.create(partyData as Omit<FeesApportionParty, "id" | "created_at" | "updated_at">);
        toast({
          title: "Party created",
          description: "The fees apportion party has been created successfully."
        });
        navigate(`/backoffice/fees-apportion-parties/${newParty.id}`);
      } else if (partyId) {
        await feesApportionPartyApi.update(partyId, partyData as Partial<FeesApportionParty>);
        toast({
          title: "Party updated",
          description: "The fees apportion party has been updated successfully."
        });
      }
    } catch (error: any) {
      console.error("Error saving party:", error);
      toast({
        title: isNewParty ? "Error creating party" : "Error updating party",
        description: error.message || `Failed to ${isNewParty ? 'create' : 'update'} fees apportion party`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!partyId) return;
    
    setConfirmDelete(false);
    try {
      await feesApportionPartyApi.delete(partyId);
      toast({
        title: "Party deleted",
        description: "The fees apportion party has been deleted successfully."
      });
      navigate('/backoffice/fees-apportion-parties');
    } catch (error: any) {
      toast({
        title: "Error deleting party",
        description: error.message || "Failed to delete fees apportion party",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setParty(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getEntityOptions = (): EntityOption[] => {
    if (selectedEntityType === 'management_company') {
      return managementCompanies.map(company => ({
        id: company.id,
        name: company.name
      }));
    } else if (selectedEntityType === 'association') {
      return associations.map(association => ({
        id: association.id,
        name: association.name
      }));
    }
    return [];
  };

  if (partyError) {
    return (
      <BackofficeLayout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <h2 className="text-2xl font-bold mb-4">Error Loading Party</h2>
          <p className="text-gray-500 mb-4">
            There was an error loading the fees apportion party data.
          </p>
          <Button onClick={() => navigate('/backoffice/fees-apportion-parties')}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Parties
          </Button>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => navigate('/backoffice/fees-apportion-parties')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">
              {isNewParty ? "Create Fees Apportion Party" : `Edit ${party.entity_name}`}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isNewParty && (
              <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </Button>
            )}
            <Button onClick={handleSave} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Party Information</CardTitle>
            <CardDescription>
              Set the details for this fees apportion party
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entity_type">Entity Type</Label>
                <Select 
                  value={selectedEntityType} 
                  onValueChange={handleEntityTypeChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Other)</SelectItem>
                    <SelectItem value="platform">Platform</SelectItem>
                    <SelectItem value="management_company">Management Company</SelectItem>
                    <SelectItem value="association">Association</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {selectedEntityType !== 'none' && selectedEntityType !== 'platform' && (
                <div className="space-y-2">
                  <Label htmlFor="entity_id">Select {selectedEntityType === 'management_company' ? 'Management Company' : 'Association'} (Optional)</Label>
                  <Select
                    value={party.entity_id?.toString() || ""}
                    onValueChange={handleEntitySelection}
                    disabled={
                      (selectedEntityType === 'management_company' && loadingManagementCompanies) ||
                      (selectedEntityType === 'association' && isLoadingAssociations)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue 
                        placeholder={`Select ${selectedEntityType === 'management_company' ? 'management company' : 'association'} (optional)`} 
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {getEntityOptions().map(entity => (
                        <SelectItem key={entity.id} value={entity.id.toString()}>
                          {entity.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {((selectedEntityType === 'management_company' && loadingManagementCompanies) ||
                    (selectedEntityType === 'association' && isLoadingAssociations)) && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Loading...
                    </div>
                  )}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea 
                  id="description"
                  name="description"
                  placeholder="Enter description" 
                  value={party.description || ''}
                  onChange={handleInputChange}
                  rows={4}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Fees Apportion Party</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this fees apportion party? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BackofficeLayout>
  );
};

export default FeesApportionPartyDetail;
